import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import RawHtml from '../Editors/RawHtml/raw-html';
import '../styles/RichContent.sass';
import './ContentWithToc.sass';

const TOC_HEADINGS_SELECTOR = 'h2, h3';

/**
 * Assigns missing ids to h2/h3 headings and collects them for the table of contents.
 */
const buildTableOfContents = (html, headingIdPrefix) => {
  if (!html || typeof html !== 'string' || typeof DOMParser === 'undefined') {
    return { html, headings: [] };
  }

  const documentFragment = new DOMParser().parseFromString(html, 'text/html');
  const headings = [];

  documentFragment
    .querySelectorAll(TOC_HEADINGS_SELECTOR)
    .forEach((heading, index) => {
      const text = heading.textContent.trim();
      if (!text) return;

      if (!heading.id) {
        heading.id = `${headingIdPrefix}-${index}`;
      }

      headings.push({
        id: heading.id,
        text,
        level: heading.tagName === 'H2' ? 2 : 3,
      });
    });

  return { html: documentFragment.body.innerHTML, headings };
};

const scrollToHeading = (id) => {
  document
    .getElementById(id)
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

/**
 * Renders HTML content (e.g. from TinyMCE) with a sticky table of contents built from h2/h3 headings.
 * The table of contents is hidden when the content has fewer headings than minHeadings.
 */
const ContentWithToc = ({
  content,
  contentClassName = 'rich-content',
  wrapperClassName = '',
  tocTitle,
  headingIdPrefix = 'content-heading',
  minHeadings = 1,
}) => {
  const { t } = useTranslation();

  const { html, headings } = useMemo(
    () => buildTableOfContents(content, headingIdPrefix),
    [content, headingIdPrefix],
  );

  const showToc = headings.length >= minHeadings && headings.length > 0;
  const title = tocTitle || t('Obsah');

  return (
    <div
      className={`content-with-toc ${showToc ? 'content-with-toc--has-toc' : ''}`.trim()}
    >
      <article
        className={`content-with-toc__content ${wrapperClassName}`.trim()}
      >
        <RawHtml className={contentClassName}>{html}</RawHtml>
      </article>

      {showToc && (
        <nav className="content-with-toc__toc" aria-label={title}>
          <div className="content-with-toc__toc-title">{title}</div>
          <ul>
            {headings.map((heading) => (
              <li
                key={heading.id}
                className={`content-with-toc__toc-item--h${heading.level}`}
              >
                <a
                  href={`#${heading.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToHeading(heading.id);
                  }}
                >
                  {heading.text}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
};

ContentWithToc.propTypes = {
  content: PropTypes.string,
  contentClassName: PropTypes.string, // classes of the rendered HTML, e.g. 'rich-content rich-content--steps'
  wrapperClassName: PropTypes.string, // classes of the content column, e.g. card styling
  tocTitle: PropTypes.string,
  headingIdPrefix: PropTypes.string,
  minHeadings: PropTypes.number,
};

export default ContentWithToc;
